/* ruleLabelMapper.js */

/**
 * @module ruleLabelMapper
 *
 * UUID ↔ label conversion for an audit-policy rule. Two functions, exact
 * inverses modulo unresolvable labels (which are reported as errors rather
 * than silently corrupting data):
 *
 *   ruleUuidsToLabels(rule, labelMaps) → {rule, errors}
 *   ruleLabelsToUuids(rule, labelMaps) → {rule, errors}
 *
 * `labelMaps` shape:
 *   {
 *     conceptItems:  {<uuid>: {label}},
 *     rsiIdsMap:     {<uuid>: {label}},
 *     commandSets:   {<uuid>: <name>},     // value→label map
 *     commandSetOs:  {<uuid>: <os>},       // command-set OS, for OS-scoped reverse lookup
 *     customFields:  {<uuid>: {name, dataType, typeInfo:{ids:[],labels:[]}}},
 *     tags:          {<id>: <name>},       // tag id→name map (PE-14307)
 *     qbColumns:     [{name, type, input}] // then/else column config (optional)
 *   }
 *
 * Property *names* are preserved across the round-trip — only UUID *values*
 * are swapped for their labels (and back): rule-level `commandSet`,
 * `conceptItemId`, `rsiId`, and `commandSetResult.{conceptItemId,
 * restSyncItemId}`. The one exception is the `customFieldValues.<uuid>`
 * expression path, where the identifier lives in the property name itself, so
 * the name segment is labelized to `customFieldValues.<name>`.
 *
 * The `ifExpression` `tagIds` condition value (an id or array of ids) is also
 * swapped for tag names and back (PE-14307), so exported tag-based policies are
 * portable across orgs where tag ids differ.
 *
 * Also walks `ifExpression` (MongoDB form) and `thenExpression` /
 * `elseExpression` (queryBuilder form), rewriting those `customFieldValues.<uuid>`
 * paths and enum-typed option-UUID values. On the labels→UUIDs (parse-back)
 * direction it also backfills the queryBuilder display props a hand-written
 * leaf may omit — `field` (mirrors `id`), `type`/`input` (from `qbColumns`),
 * and group `valid` — so a JSON-saved rule matches the shape form-mode persists.
 *
 * Errors are non-fatal — the partial result is returned so callers can decide
 * whether to push it into the entity.
 */
const _ = require("lodash");

const DIRECTION_UUIDS_TO_LABELS = "uuidsToLabels";
const DIRECTION_LABELS_TO_UUIDS = "labelsToUuids";

const CUSTOM_FIELD_PATH_PATTERN = /^customFieldValues\.(.+)$/;

// Command-set sentinel value → label. These aren't real command sets (not in
// the command-set feed), so they must be merged into the commandSets label map
// for both the labelizer round-trip and the dynamic-schema enum. Labels match
// formatters/index.js. `all` is also seeded in cmdSetValLabelMap; the other two
// are only added to per-rule dropdowns at render time, so they'd otherwise be
// missing here.
const COMMAND_SET_SENTINELS = {
    all: "All",
    allConfig: "All Configuration",
    allOperational: "All Operational",
};

/**
 * For each enum-typed custom field, build option-uuid↔option-label maps:
 *   optionUuidToLabelByField[fieldUuid] = {<optionUuid>: <optionLabel>}
 *   optionLabelToUuidByField[fieldUuid] = {<optionLabel>: <optionUuid>}
 */
const _buildCustomFieldOptionMaps = (customFieldsByUuid) => {
    const optionUuidToLabelByField = {};
    const optionLabelToUuidByField = {};
    _.each(customFieldsByUuid || {}, (customField, fieldUuid) => {
        const optionIds = customField && customField.typeInfo && customField.typeInfo.ids;
        const optionLabels = customField && customField.typeInfo && customField.typeInfo.labels;
        if (!_.isArray(optionIds) || !_.isArray(optionLabels)) {
            return;
        }
        const optionUuidToLabel = {};
        const optionLabelToUuid = {};
        _.each(optionIds, (optionUuid, optionIndex) => {
            const optionLabel = optionLabels[optionIndex];
            if (optionLabel === null || optionLabel === undefined) {
                return;
            }
            optionUuidToLabel[optionUuid] = optionLabel;
            optionLabelToUuid[optionLabel] = optionUuid;
        });
        optionUuidToLabelByField[fieldUuid] = optionUuidToLabel;
        optionLabelToUuidByField[fieldUuid] = optionLabelToUuid;
    });
    return {optionUuidToLabelByField, optionLabelToUuidByField};
};

/**
 * Recursively walk an expression tree (either MongoDB form or queryBuilder
 * form) and rewrite each leaf via `rewriteLeaf`. `normalizeGroup` (queryBuilder
 * form only) post-processes each group node — used on parse-back to backfill
 * the transient `valid` flag the JSON view no longer hides.
 */
const _walkExpression = (expression, rewriteLeaf, isMongoForm, normalizeGroup) => {
    if (!_.isObject(expression) || _.isArray(expression)) {
        return expression;
    }
    if (isMongoForm) {
        if (expression.$and) {
            return {
                $and: _.map(expression.$and, (sub) => _walkExpression(sub, rewriteLeaf, true)),
            };
        }
        if (expression.$or) {
            return {
                $or: _.map(expression.$or, (sub) => _walkExpression(sub, rewriteLeaf, true)),
            };
        }
        return rewriteLeaf(expression, "mongo");
    }
    if (expression.condition && _.isArray(expression.rules)) {
        const walkedGroup = {
            ...expression,
            rules: _.map(expression.rules, (sub) =>
                _walkExpression(sub, rewriteLeaf, false, normalizeGroup)
            ),
        };
        return normalizeGroup ? normalizeGroup(walkedGroup) : walkedGroup;
    }
    return rewriteLeaf(expression, "qb");
};

const _rewriteMongoLeaf = (leaf, rewriteField, rewriteValue) => {
    const rewrittenLeaf = {};
    _.each(leaf, (operatorObject, fieldName) => {
        const rewrittenField = rewriteField(fieldName) || fieldName;
        if (_.isObject(operatorObject) && !_.isArray(operatorObject)) {
            const rewrittenOperators = {};
            _.each(operatorObject, (operatorValue, operatorName) => {
                rewrittenOperators[operatorName] = rewriteValue(fieldName, operatorValue);
            });
            rewrittenLeaf[rewrittenField] = rewrittenOperators;
        } else {
            rewrittenLeaf[rewrittenField] = rewriteValue(fieldName, operatorObject);
        }
    });
    return rewrittenLeaf;
};

/**
 * PE-14307: build a case-insensitive tag-value resolver over `map`. Tries an
 * exact match first, then a case-insensitive fallback, so tag names resolve
 * regardless of case — matching the backend / device-import behavior. Unmatched
 * (and non-string) values are returned as-is. Used for both name→id (import,
 * JSON save) and id→name (export, JSON open); the id direction is unaffected in
 * practice since ids are unique lowercase uuids.
 */
const _makeTagResolver = (map) => {
    const source = map || {};
    const ciIndex = {};
    _.each(source, (value, key) => {
        ciIndex[String(key).toLowerCase()] = value;
    });
    return (value) => {
        if (!_.isString(value)) {
            return value;
        }
        if (source[value] !== undefined) {
            return source[value];
        }
        const ciMatch = ciIndex[value.toLowerCase()];
        return ciMatch !== undefined ? ciMatch : value;
    };
};

/**
 * PE-14307: convert only the `ifExpression` `tagIds` condition value(s) via
 * `tagMap` (name→id on import, id→name on export), leaving field names and all
 * other leaves untouched. Matching is case-insensitive. Unmatched values are
 * passed through as-is. Returns a new expression (the input is not mutated).
 *
 * Standalone from ruleUuidsToLabels/ruleLabelsToUuids so the audit-policy file
 * import — which already does its own name→id conversion for concept items,
 * RSIs and command sets — can reuse the exact tag-swap logic without
 * re-running (and conflicting with) those other conversions.
 *
 * @param {Object} expression MongoDB-form ifExpression
 * @param {Object} tagMap     lookup keyed by the source value (name or id)
 * @returns {Object} rewritten expression
 */
const convertExpressionTags = (expression, tagMap) => {
    if (!_.isObject(expression)) {
        return expression;
    }
    const convertTag = _makeTagResolver(tagMap);
    return _walkExpression(
        expression,
        (leaf) =>
            _rewriteMongoLeaf(
                leaf,
                (fieldName) => fieldName,
                (fieldName, fieldValue) => {
                    if (fieldName !== "tagIds") {
                        return fieldValue;
                    }
                    return _.isArray(fieldValue)
                        ? _.map(fieldValue, convertTag)
                        : convertTag(fieldValue);
                }
            ),
        true
    );
};

// `field`/`type`/`input` are queryBuilder bookkeeping derived entirely from the
// column config — `field` mirrors `id`; `type`/`input` come from the column. We
// normalize all three from the canonical source rather than trust hand-edited
// values: `field` is rewritten in lockstep with `id`, and on parse-back
// (`backfill` provided) `type`/`input` are overwritten from the resolved column
// so a JSON-saved leaf can't carry bad bookkeeping and always matches what
// form-mode persists. Leaves on an unknown column (no `backfill` hit) keep
// whatever was there — queryBuilder re-derives on the next form render.
const _rewriteQueryBuilderLeaf = (leaf, rewriteField, rewriteValue, backfill) => {
    if (!leaf || !leaf.id) {
        return leaf;
    }
    const rewrittenId = rewriteField(leaf.id) || leaf.id;
    const rewritten = {
        ...leaf,
        id: rewrittenId,
        field: rewrittenId,
        value: rewriteValue(leaf.id, leaf.value),
    };
    const column = backfill && backfill[rewrittenId];
    if (column) {
        rewritten.type = column.type;
        if (column.input !== undefined) {
            rewritten.input = column.input;
        }
    }
    return rewritten;
};

/**
 * Rewrite a `customFieldValues.<uuid-or-name>` path between UUID and label
 * forms. Returns null if `fieldPath` is not a customFieldValues path or the
 * inner key can't be resolved.
 */
const _rewriteCustomFieldPath = (
    fieldPath,
    direction,
    customFieldNameToUuid,
    customFieldsByUuid
) => {
    const match = CUSTOM_FIELD_PATH_PATTERN.exec(fieldPath || "");
    if (!match) {
        return null;
    }
    const innerKey = match[1];
    if (direction === DIRECTION_UUIDS_TO_LABELS) {
        const customField = customFieldsByUuid[innerKey];
        return customField && customField.name ? "customFieldValues." + customField.name : null;
    }
    const resolvedUuid = customFieldNameToUuid[innerKey];
    return resolvedUuid ? "customFieldValues." + resolvedUuid : null;
};

/**
 * Resolve a command-set *label* to its UUID, scoped by the rule's OS. Two
 * command sets in different OS families can share a name, so a global
 * name→UUID lookup would collide (last one wins). The form's command-set
 * dropdown is already OS-scoped (`_getCommandSets(osName)` filters by the
 * rule's OS); this mirrors that on the JSON parse-back path.
 *
 * Sentinels (`all`/`allConfig`/`allOperational`) match regardless of OS.
 * Returns `{uuid}` on success, or `{error}` when the label is unknown for the
 * rule's OS or matches more than one command set (ambiguous).
 *
 * @param {string} label  command-set label as shown in the JSON editor
 * @param {string} ruleOs the rule's `os` ("" / "all" / undefined → unscoped)
 * @param {object} labelMaps {commandSets:{uuid:name}, commandSetOs:{uuid:os}}
 * @returns {{uuid: string}|{error: string}}
 */
const resolveCommandSetUuid = (label, ruleOs, labelMaps) => {
    if (label === null || label === undefined || label === "") {
        return {uuid: label};
    }
    const commandSetUuidToLabel = (labelMaps && labelMaps.commandSets) || {};
    const commandSetOs = (labelMaps && labelMaps.commandSetOs) || {};
    const candidates = _.keys(_.pickBy(commandSetUuidToLabel, (name) => name === label));
    if (!candidates.length) {
        return {error: `Unknown commandSet "${label}"`};
    }
    const osScoped = !ruleOs || ruleOs === "all";
    const scoped = _.filter(
        candidates,
        (uuid) =>
            // Sentinels carry no OS and always qualify.
            _.has(COMMAND_SET_SENTINELS, uuid) || osScoped || commandSetOs[uuid] === ruleOs
    );
    if (scoped.length === 1) {
        return {uuid: scoped[0]};
    }
    if (!scoped.length) {
        return {error: `Unknown commandSet "${label}" for OS "${ruleOs}"`};
    }
    return {
        error: `Ambiguous commandSet "${label}" — matches ${scoped.length} command sets; set the rule's OS to disambiguate`,
    };
};

const _convertRule = (rule, labelMaps, direction) => {
    const errors = [];
    if (!_.isObject(rule)) {
        return {rule, errors};
    }
    const convertedRule = _.cloneDeep(rule);
    const toLabels = direction === DIRECTION_UUIDS_TO_LABELS;

    // Inverse maps for rule-level fields — built once per call.
    const customFieldsByUuid = labelMaps.customFields || {};
    const customFieldNameToUuid = _.invert(_.mapValues(customFieldsByUuid, "name"));
    const {optionUuidToLabelByField, optionLabelToUuidByField} =
        _buildCustomFieldOptionMaps(customFieldsByUuid);

    const commandSetUuidToLabel = labelMaps.commandSets || {};
    const conceptItemUuidToLabel = _.mapValues(labelMaps.conceptItems || {}, "label");
    const conceptItemLabelToUuid = _.invert(conceptItemUuidToLabel);
    const rsiUuidToLabel = _.mapValues(labelMaps.rsiIdsMap || {}, "label");
    const rsiLabelToUuid = _.invert(rsiUuidToLabel);

    // PE-14307: tag id↔name for the ifExpression `tagIds` condition value.
    const tagIdToName = labelMaps.tags || {};
    const resolveTag = _makeTagResolver(toLabels ? tagIdToName : _.invert(tagIdToName));

    // queryBuilder column config (then/elseExpression), keyed by id — used on
    // parse-back to backfill each leaf's `type`/`input` so a JSON-saved rule
    // matches the shape form-mode persists.
    const qbColumnsById = toLabels ? null : _.keyBy(labelMaps.qbColumns || [], "name");
    const normalizeGroup = toLabels
        ? null
        : (group) => (group.valid === undefined ? {...group, valid: true} : group);

    // Convert host[key] in place between its UUID and label form via `lookup`,
    // *preserving the property name*. On parse-back (labels→UUIDs) an
    // unresolved value is reported via `errorKind` so a bogus hand-typed label
    // is caught; on the uuids→labels direction a stale UUID is tolerated and
    // left as-is (no error, just unlabelized).
    const relabelValue = (host, key, lookup, errorPath, errorKind) => {
        const raw = host && host[key];
        if (!raw) {
            return;
        }
        const resolved = lookup[raw];
        if (resolved) {
            host[key] = resolved;
        } else if (errorKind) {
            errors.push({path: errorPath, message: `Unknown ${errorKind} "${raw}"`});
        }
    };

    // ----- rule-level UUID-valued fields -----
    // Property names are kept unchanged; only the UUID *value* is swapped for
    // its label (and back). The `customFieldValues.<id>` expression paths
    // (below) are the sole place the identifier lives in the name itself.
    const conceptItemMap = toLabels ? conceptItemUuidToLabel : conceptItemLabelToUuid;
    const rsiMap = toLabels ? rsiUuidToLabel : rsiLabelToUuid;
    // commandSet display (uuid→label) is a plain lookup; parse-back (label→uuid)
    // is OS-scoped to avoid same-name collisions across OS families.
    if (toLabels) {
        relabelValue(convertedRule, "commandSet", commandSetUuidToLabel, "/commandSet", null);
    } else if (convertedRule.commandSet) {
        const {uuid, error} = resolveCommandSetUuid(
            convertedRule.commandSet,
            convertedRule.os,
            labelMaps
        );
        if (error) {
            errors.push({path: "/commandSet", message: error});
        } else if (uuid !== undefined) {
            convertedRule.commandSet = uuid;
        }
    }
    relabelValue(
        convertedRule,
        "conceptItemId",
        conceptItemMap,
        "/conceptItemId",
        toLabels ? null : "conceptItem"
    );
    relabelValue(convertedRule, "rsiId", rsiMap, "/rsiId", toLabels ? null : "restSyncItem");
    if (convertedRule.commandSetResult) {
        relabelValue(
            convertedRule.commandSetResult,
            "conceptItemId",
            conceptItemMap,
            "/commandSetResult/conceptItemId",
            toLabels ? null : "conceptItem"
        );
        relabelValue(
            convertedRule.commandSetResult,
            "restSyncItemId",
            rsiMap,
            "/commandSetResult/restSyncItemId",
            toLabels ? null : "restSyncItem"
        );
    }

    // ----- expressions -----
    const rewriteField = (fieldName) => {
        const rewritten = _rewriteCustomFieldPath(
            fieldName,
            direction,
            customFieldNameToUuid,
            customFieldsByUuid
        );
        if (!toLabels && !rewritten && /^customFieldValues\./.test(fieldName)) {
            errors.push({
                path: `/expression/${fieldName}`,
                message: `Unknown custom field "${fieldName.split(".")[1]}"`,
            });
        }
        return rewritten;
    };

    const rewriteValue = (fieldName, fieldValue) => {
        if (fieldName === "tagIds") {
            return _.isArray(fieldValue) ? _.map(fieldValue, resolveTag) : resolveTag(fieldValue);
        }
        const match = CUSTOM_FIELD_PATH_PATTERN.exec(fieldName || "");
        if (!match) {
            return fieldValue;
        }
        let fieldUuid = match[1];
        if (!toLabels && customFieldNameToUuid[fieldUuid]) {
            fieldUuid = customFieldNameToUuid[fieldUuid];
        }
        const optionUuidToLabel = optionUuidToLabelByField[fieldUuid];
        const optionLabelToUuid = optionLabelToUuidByField[fieldUuid];
        if (!optionUuidToLabel && !optionLabelToUuid) {
            return fieldValue;
        }

        const convertOptionValue = (singleValue) => {
            if (!_.isString(singleValue)) {
                return singleValue;
            }
            if (toLabels) {
                return optionUuidToLabel[singleValue] || singleValue;
            }
            // Not an enum option label — leave as-is (covers free-text).
            return optionLabelToUuid[singleValue] || singleValue;
        };

        return _.isArray(fieldValue)
            ? _.map(fieldValue, convertOptionValue)
            : convertOptionValue(fieldValue);
    };

    if (convertedRule.ifExpression) {
        convertedRule.ifExpression = _walkExpression(
            convertedRule.ifExpression,
            (leaf) => _rewriteMongoLeaf(leaf, rewriteField, rewriteValue),
            true
        );
    }
    if (convertedRule.thenExpression) {
        convertedRule.thenExpression = _walkExpression(
            convertedRule.thenExpression,
            (leaf) => _rewriteQueryBuilderLeaf(leaf, rewriteField, rewriteValue, qbColumnsById),
            false,
            normalizeGroup
        );
    }
    if (convertedRule.elseExpression) {
        convertedRule.elseExpression = _walkExpression(
            convertedRule.elseExpression,
            (leaf) => _rewriteQueryBuilderLeaf(leaf, rewriteField, rewriteValue, qbColumnsById),
            false,
            normalizeGroup
        );
    }

    return {rule: convertedRule, errors};
};

const ruleUuidsToLabels = (rule, labelMaps) =>
    _convertRule(rule, labelMaps || {}, DIRECTION_UUIDS_TO_LABELS);

const ruleLabelsToUuids = (rule, labelMaps) =>
    _convertRule(rule, labelMaps || {}, DIRECTION_LABELS_TO_UUIDS);

module.exports = {
    ruleUuidsToLabels,
    ruleLabelsToUuids,
    convertExpressionTags,
    resolveCommandSetUuid,
    COMMAND_SET_SENTINELS,
};
